// P4-SCHED — the schedule RULESET (docs/implementation/P4_SCHEDULE_DURATION.md,
// docs/implementation/SCHEDULE_INTELLIGENCE_MODULE.md §10 / §12 / §41 / §42 / §50).
//
// A schedule is generated from scope, never typed from scratch: each activity
// type below says which physical quantity produces it (a Room Element basis,
// the drawn wall conditions, or the placed openings), or that it is a
// fixed-duration phase activity / a zero-duration milestone. Activities are
// generated only when their scope exists (§42 — "no ductwork quantity, no
// Install Ductwork"). Dependencies are RULES (FS / SS / FF / SF + lag), applied
// only where both endpoints were generated.
//
// Nothing here is a duration. Production standards (units per crew-HOUR,
// fixed phase durations, minimums) live in ProductionRateStandards (db/78),
// versioned, entered by the product owner — an activity whose standard does
// not exist yet reads "Duration required", never a guess.

export const SCHEDULE_ENGINE_VERSION = "1.2.0"; // 1.1.0 (2026-09-14): Release 2-A procurement chain · 1.2.0 (2026-09-22): Release 2-B MEP quantities

export const SCHEDULE_PHASES = [
  { key: "preconstruction", label: "Preconstruction", order: 1 },
  { key: "design",          label: "Design",          order: 2 },
  { key: "procurement",     label: "Procurement",     order: 3 },
  { key: "manufacturing",   label: "Manufacturing",   order: 4 },
  { key: "construction",    label: "Construction",    order: 5 },
  { key: "startup",         label: "Startup",         order: 6 },
  { key: "commissioning",   label: "Commissioning",   order: 7 },
  { key: "closeout",        label: "Closeout",        order: 8 },
];
export const PHASE_BY_KEY = Object.fromEntries(SCHEDULE_PHASES.map(p => [p.key, p]));

export const SCHEDULE_DISCIPLINES = [
  { key: "general",  label: "General" },
  { key: "arch",     label: "Architectural" },
  { key: "mech",     label: "Mechanical" },
  { key: "elec",     label: "Electrical" },
  { key: "process",  label: "Process utilities" },
  { key: "controls", label: "Controls" },
  { key: "cx",       label: "Commissioning" },
];

// Scope classification BEFORE prediction (§9). Several attributes, not one
// exclusive label; the vocabulary matches the historical dataset's
// scope_class values so comparables can be matched later.
export const DELIVERY_MODELS = [
  { key: "core_envelope",            label: "Core cleanroom / envelope" },
  { key: "turnkey_full_scope",       label: "Turnkey full scope" },
  { key: "mep_heavy",                label: "MEP-heavy" },
  { key: "material_only",            label: "Material only" },
  { key: "labor_install_only",       label: "Labor / install only" },
  { key: "design_only",              label: "Design only" },
  { key: "cold_room",                label: "Cold room" },
  { key: "change_order",             label: "Alteration / change order" },
  { key: "shutdown",                 label: "Shutdown work" },
  { key: "phased_occupied",          label: "Phased, occupied facility" },
  { key: "conceptual_full_facility", label: "Conceptual full facility" },
];
export const DEFAULT_SCOPE_PROFILE = Object.freeze({
  deliveryModel: "core_envelope",
  occupiedFacility: false,
  shutdownRequired: false,
  phased: false,
  validationRequired: false,
  unionLabor: false,
});
export const SCOPE_PROFILE_FLAGS = [
  { key: "occupiedFacility",   label: "Occupied facility" },
  { key: "shutdownRequired",   label: "Shutdown required" },
  { key: "phased",             label: "Phased construction" },
  { key: "validationRequired", label: "Validation required" },
  { key: "unionLabor",         label: "Union labor" },
];

// kind: 'quantity' (duration from quantity ÷ production standard),
//       'fixed'    (duration from the standard's fixed days — Design, Punch …),
//       'milestone' (zero duration).
// source: where the generator reads the quantity —
//       'wall_conditions'      drawn linear wall runs (LF × height → SF)
//       'element:<basis>'      Room Elements of that quantity basis (effective qty)
//       'openings:<kind>'      placed openings of that kind (count)
//       'mep:<what>[:<sub>]'   Release 2-B MEP quantities (src/utils/mepScheduleTakeoff.js):
//                              route LF by piping group, refrigerant line-set LF,
//                              new panels, wired circuits, equipment connections
// requires: generate this fixed activity only when one of these types exists.
// keyPrefix: the ActivityKey prefix ('arch' when omitted — the Release 1
//       ruleset keys every generated type 'arch:<TYPE>', whatever its
//       discipline; Release 2-B types are 'mep:<TYPE>'). Keys carry no record
//       ids, so a copied revision (db/90) keeps them verbatim.
export const ACTIVITY_TYPES = [
  { key: "NTP",                    name: "Contract / NTP",                 phase: "preconstruction", discipline: "general", kind: "milestone" },
  { key: "DESIGN",                 name: "Design",                         phase: "design",          discipline: "general", kind: "fixed" },
  { key: "SUBMITTALS",             name: "Submittals / approval",          phase: "design",          discipline: "general", kind: "fixed" },
  { key: "PANEL_RELEASE",          name: "Panel procurement release",      phase: "procurement",     discipline: "arch",    kind: "fixed", requires: ["WALL_PANELS", "CEILING_PANELS"] },
  { key: "PANEL_FABRICATION",      name: "Panel fabrication",              phase: "manufacturing",   discipline: "arch",    kind: "fixed", requires: ["WALL_PANELS", "CEILING_PANELS"] },
  { key: "MOBILIZATION",           name: "Mobilization",                   phase: "construction",    discipline: "general", kind: "fixed" },
  { key: "WALL_PANELS",            name: "Wall panel installation",        phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "WALL_SF",        unit: "SF", source: "wall_conditions" },
  { key: "WALL_FINISHES",          name: "Wall finishes / liner",          phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "WALL_FINISH_SF", unit: "SF", source: "element:wall_area" },
  { key: "CEILING_SUPPORT",        name: "Ceiling support / grid",         phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "CEILING_SF",     unit: "SF", source: "element:ceiling_area" },
  { key: "CEILING_PANELS",         name: "Ceiling panel installation",     phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "CEILING_SF",     unit: "SF", source: "element:ceiling_area" },
  { key: "FLOORING",               name: "Flooring",                       phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "FLOOR_SF",       unit: "SF", source: "element:floor_area" },
  { key: "DOORS",                  name: "Door / frame installation",      phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "DOOR_COUNT",     unit: "EA", source: "openings:door" },
  { key: "WINDOWS",                name: "Window installation",            phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "WINDOW_COUNT",   unit: "EA", source: "openings:window" },
  { key: "PASS_THRUS",             name: "Pass-through installation",      phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "PASS_THRU_COUNT", unit: "EA", source: "openings:pass_through" },
  { key: "HEPA_INSTALL",           name: "HEPA housing / filter installation", phase: "construction", discipline: "mech",   kind: "quantity", quantityType: "HEPA_COUNT",     unit: "EA", source: "element:engine_hepa" },
  { key: "LIGHTING_INSTALL",       name: "Lighting installation",          phase: "construction",    discipline: "elec",    kind: "quantity", quantityType: "LIGHT_COUNT",    unit: "EA", source: "element:engine_light" },
  { key: "COVE_BASE",              name: "Cove / base installation",       phase: "construction",    discipline: "arch",    kind: "quantity", quantityType: "COVE_LF",        unit: "LF", source: "element:perimeter" },
  // Release 2-B (§4, §51) — MEP installation from the canonical MEP records.
  // One activity per type, summed across systems (the detail lists each
  // system / panel / record), the way the architectural types sum across rooms.
  { key: "MEP_PIPING_HYDRONIC",       name: "Hydronic piping (CHW / HHW)",       phase: "construction", discipline: "mech",    kind: "quantity", quantityType: "HYDRONIC_PIPE_LF",         unit: "LF", source: "mep:piping:hydronic",         keyPrefix: "mep" },
  { key: "MEP_PIPING_PROCESS_WATER",  name: "Process / domestic water piping",   phase: "construction", discipline: "process", kind: "quantity", quantityType: "PROCESS_WATER_PIPE_LF",    unit: "LF", source: "mep:piping:process_water",    keyPrefix: "mep" },
  { key: "MEP_PIPING_GAS",            name: "Process gas / vacuum piping",       phase: "construction", discipline: "process", kind: "quantity", quantityType: "GAS_PIPE_LF",              unit: "LF", source: "mep:piping:gas",              keyPrefix: "mep" },
  { key: "MEP_PIPING_STEAM",          name: "Steam piping",                      phase: "construction", discipline: "process", kind: "quantity", quantityType: "STEAM_PIPE_LF",            unit: "LF", source: "mep:piping:steam",            keyPrefix: "mep" },
  { key: "MEP_PIPING_DRAIN",          name: "Process drain piping",              phase: "construction", discipline: "process", kind: "quantity", quantityType: "DRAIN_PIPE_LF",            unit: "LF", source: "mep:piping:drain",            keyPrefix: "mep" },
  { key: "MEP_DUCT_PROCESS",          name: "Process exhaust / vent ductwork",   phase: "construction", discipline: "process", kind: "quantity", quantityType: "PROCESS_DUCT_LF",          unit: "LF", source: "mep:piping:duct",             keyPrefix: "mep" },
  { key: "MEP_REFRIGERANT",           name: "Refrigerant line sets",             phase: "construction", discipline: "mech",    kind: "quantity", quantityType: "REFRIGERANT_RUN_LF",       unit: "LF", source: "mep:refrigerant",             keyPrefix: "mep" },
  { key: "ELEC_PANELS",               name: "Electrical panel set",              phase: "construction", discipline: "elec",    kind: "quantity", quantityType: "PANEL_COUNT",              unit: "EA", source: "mep:panels",                  keyPrefix: "mep" },
  { key: "ELEC_BRANCH_CIRCUITS",      name: "Branch circuit rough-in / wiring",  phase: "construction", discipline: "elec",    kind: "quantity", quantityType: "CIRCUIT_COUNT",            unit: "EA", source: "mep:circuits",                keyPrefix: "mep" },
  { key: "ELEC_EQUIP_CONNECTIONS",    name: "Equipment electrical connections",  phase: "construction", discipline: "elec",    kind: "quantity", quantityType: "ELEC_CONNECTION_COUNT",    unit: "EA", source: "mep:connections:electrical",  keyPrefix: "mep" },
  { key: "UTILITY_EQUIP_CONNECTIONS", name: "Equipment utility connections",     phase: "construction", discipline: "process", kind: "quantity", quantityType: "UTILITY_CONNECTION_COUNT", unit: "EA", source: "mep:connections:utility",     keyPrefix: "mep" },
  { key: "PUNCH",                  name: "Punch list",                     phase: "closeout",        discipline: "general", kind: "fixed" },
  { key: "STARTUP",                name: "Startup allowance",              phase: "startup",         discipline: "cx",      kind: "fixed" },
  { key: "COMMISSIONING",          name: "Commissioning allowance",        phase: "commissioning",   discipline: "cx",      kind: "fixed" },
  { key: "SUBSTANTIAL_COMPLETION", name: "Substantial completion",         phase: "closeout",        discipline: "general", kind: "milestone" },
  // Release 2-A (§39) — the procurement chain's fixed-duration steps, one
  // standard each, applied PER EQUIPMENT RECORD by src/utils/procurementSchedule.js
  // (perEquipment: the architectural generator never emits these as single
  // activities). Fabrication has no standard: it is the record's own lead time.
  { key: "EQUIP_SUBMITTAL", name: "Equipment submittal (per record)",          phase: "design",       discipline: "general", kind: "fixed", perEquipment: true },
  { key: "EQUIP_APPROVAL",  name: "Equipment submittal approval (per record)", phase: "design",       discipline: "general", kind: "fixed", perEquipment: true },
  { key: "EQUIP_SHIPPING",  name: "Equipment shipping / transit (per record)", phase: "procurement",  discipline: "general", kind: "fixed", perEquipment: true },
  { key: "EQUIP_SET",       name: "Equipment set / rig (per record)",          phase: "construction", discipline: "general", kind: "fixed", perEquipment: true },
];
export const ACTIVITY_TYPE_BY_KEY = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.key, t]));
export const activityKeyFor = (typeKey) => `${ACTIVITY_TYPE_BY_KEY[typeKey]?.keyPrefix || "arch"}:${typeKey}`;

// Which activity types a delivery model generates. Anything not listed is
// generated when its scope exists. 'design_only' is the one profile that
// removes construction outright; the others are future rules, not guesses.
export const PROFILE_EXCLUSIONS = {
  design_only: new Set(ACTIVITY_TYPES.filter(t => !["NTP", "DESIGN", "SUBMITTALS", "SUBSTANTIAL_COMPLETION"].includes(t.key)).map(t => t.key)),
};

export const RELATIONSHIP_TYPES = [
  { key: "FS", label: "Finish → Start" },
  { key: "SS", label: "Start → Start" },
  { key: "FF", label: "Finish → Finish" },
  { key: "SF", label: "Start → Finish" },
];

// Release 2-B: the utility distribution equipment utility hook-ups wait on,
// and every rough-in type (installed in the walls / overhead).
export const MEP_UTILITY_DISTRIBUTION = ["MEP_PIPING_HYDRONIC", "MEP_PIPING_PROCESS_WATER", "MEP_PIPING_GAS", "MEP_PIPING_STEAM", "MEP_PIPING_DRAIN", "MEP_DUCT_PROCESS"];
export const MEP_ROUGH_IN = [...MEP_UTILITY_DISTRIBUTION, "MEP_REFRIGERANT", "ELEC_BRANCH_CIRCUITS"];

// [predecessor, successor, type, lagDays] — applied only where both exist.
// The panel chain (§12): design → release → fabricate → install; ceiling
// closes after its support; HEPAs and lights after the ceiling; cove after
// floors (or after the ceiling when there is no flooring activity); every
// construction install feeds Punch → Startup → Commissioning → completion.
export const DEPENDENCY_RULES = [
  ["NTP", "DESIGN", "FS", 0],
  ["DESIGN", "SUBMITTALS", "FS", 0],
  ["SUBMITTALS", "PANEL_RELEASE", "FS", 0],
  ["PANEL_RELEASE", "PANEL_FABRICATION", "FS", 0],
  ["SUBMITTALS", "MOBILIZATION", "FS", 0],
  ["PANEL_FABRICATION", "WALL_PANELS", "FS", 0],
  ["MOBILIZATION", "WALL_PANELS", "FS", 0],
  ["MOBILIZATION", "FLOORING", "FS", 0],
  ["WALL_PANELS", "WALL_FINISHES", "SS", 2],
  ["WALL_PANELS", "CEILING_SUPPORT", "SS", 2],
  ["CEILING_SUPPORT", "CEILING_PANELS", "FS", 0],
  ["PANEL_FABRICATION", "CEILING_PANELS", "FS", 0],
  ["WALL_PANELS", "DOORS", "FS", 0],
  ["WALL_PANELS", "WINDOWS", "FS", 0],
  ["WALL_PANELS", "PASS_THRUS", "FS", 0],
  ["CEILING_PANELS", "HEPA_INSTALL", "FS", 0],
  ["CEILING_PANELS", "LIGHTING_INSTALL", "FS", 0],
  ["CEILING_PANELS", "FLOORING", "FS", 0],
  ["FLOORING", "COVE_BASE", "FS", 0],
  ["CEILING_PANELS", "COVE_BASE", "FS", 0],
  ["WALL_FINISHES", "COVE_BASE", "FS", 0],
  ["PUNCH", "STARTUP", "FS", 0],
  ["STARTUP", "COMMISSIONING", "FS", 0],
  ["COMMISSIONING", "SUBSTANTIAL_COMPLETION", "FS", 0],
  ["PUNCH", "SUBSTANTIAL_COMPLETION", "FS", 0],
  ["SUBMITTALS", "SUBSTANTIAL_COMPLETION", "FS", 0],
  // Release 2-B (§12 "Install Walls SS → MEP Rough-In", "Overhead MEP
  // Completion FS → Ceiling Closure"): rough-in starts after mobilization and
  // two days into the walls; overhead rough-in finishes before the ceiling
  // panels close (drain piping runs below — it does not gate the ceiling).
  // Panels are set before branch circuits start; equipment hook-ups follow the
  // distribution that feeds them (and each record's own set — the procurement
  // cross-link in generateScheduleProposal).
  ...MEP_ROUGH_IN.flatMap(k => [["MOBILIZATION", k, "FS", 0], ["WALL_PANELS", k, "SS", 2]]),
  ...MEP_ROUGH_IN.filter(k => k !== "MEP_PIPING_DRAIN").map(k => [k, "CEILING_PANELS", "FS", 0]),
  ["MOBILIZATION", "ELEC_PANELS", "FS", 0],
  ["ELEC_PANELS", "ELEC_BRANCH_CIRCUITS", "SS", 0],
  ["MOBILIZATION", "ELEC_EQUIP_CONNECTIONS", "FS", 0],
  ["ELEC_PANELS", "ELEC_EQUIP_CONNECTIONS", "FS", 0],
  ["ELEC_BRANCH_CIRCUITS", "ELEC_EQUIP_CONNECTIONS", "FS", 0],
  ["MOBILIZATION", "UTILITY_EQUIP_CONNECTIONS", "FS", 0],
  ...MEP_UTILITY_DISTRIBUTION.map(k => [k, "UTILITY_EQUIP_CONNECTIONS", "FS", 0]),
];
// Every generated construction-phase install activity precedes PUNCH (FS).
export const PUNCH_PREDECESSOR_PHASES = new Set(["construction"]);

// Why an estimator changed a calculated duration (§19) — the difference is
// information, so the reason travels with the override.
export const OVERRIDE_REASON_CODES = [
  { key: "occupied_facility",   label: "Occupied facility restrictions" },
  { key: "shift_restriction",   label: "Shift / hours restriction" },
  { key: "crew_availability",   label: "Crew availability" },
  { key: "congestion",          label: "Congestion / stacked trades" },
  { key: "procurement",         label: "Procurement / delivery" },
  { key: "owner_constraint",    label: "Owner constraint / shutdown window" },
  { key: "scope_uncertainty",   label: "Scope uncertainty" },
  { key: "historical_judgment", label: "Historical judgment" },
  { key: "other",               label: "Other (see note)" },
];

export const phaseOrder = (key) => PHASE_BY_KEY[key]?.order ?? 99;
